import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ProductCard, type Product } from './ProductCard';
import { getRecommendations } from '@/lib/tracking';

interface Props {
  /** Product id to keep out of the suggestions (e.g. the one being viewed). */
  excludeId?: string;
  limit?: number;
  className?: string;
}

/**
 * "Recommended for you" rail. Reads the visitor's view + dwell-time history
 * (localStorage) and surfaces the best matches from the catalogue. Falls back
 * to popular items before any history exists, so it's never empty.
 */
export function RecommendedForYou({ excludeId, limit = 5, className = '' }: Props) {
  // Real, live catalogue - recommendations are never demo data.
  const { data: pool = [] } = useQuery<Product[]>({
    queryKey: ['products', 'recommend'],
    queryFn: async () => (await api.get('/products')).data,
    retry: false,
  });
  const items = useMemo(() => {
    const recs = getRecommendations(pool, limit + (excludeId ? 1 : 0));
    return recs.filter((p) => p.id !== excludeId).slice(0, limit);
  }, [pool, excludeId, limit]);

  if (items.length === 0) return null;

  return (
    <section className={`bg-cream border-t border-warmline py-14 ${className}`}>
      <div className="max-w-[1800px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="display text-2xl md:text-3xl text-ink">
              Recommended for you.
            </h2>
          </div>
          <Link to="/c/all" className="btn btn-outline btn-sm self-end shrink-0">See all →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-8">
          {items.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
}
